// THE CLOSING ACCOUNT — the run's own answer to "did you do the job I gave you".
//
// `close_pass` is a genuine tool-layer guarantee (routes/agentTools.ts): it refuses and
// re-prompts until the account names every in-scope ref, and only then writes
// `stats.pass_closed` + `stats.closing_account`. The artefact is enforced, present on 12 of 15
// URSC runs and up to 4 KB long — and until now ZERO pixels of it appeared anywhere in the app
// (tab-agent RESEARCH §3.1 G1).
//
// F8: ONE parser, shared. Agent's run expansion and Proposals §6.2 both render this text, and
// two parsers of one text format was curator collision #6. No `pg`, no React.
//
// The three disciplines write three shapes, all of them `key: rest` lines:
//   docs             `DOC-032: 4 proposals, 0 questions — an introduction meeting presentation …`
//                    with a bare `Category sweep:` heading and `Product & features: covered by 0 proposals — …`
//   register         `REQ-001: Module 1: Requirements Extraction & Compliance Assessment`
//   fact_namespaces  a bare `stakeholder` heading, then `sponsor: Thomas Combemale (…)`
//
// The tally is only ever read where the account STATES one. A line that carries no number gets
// `null`, and the reader shows nothing — never a fabricated 0, which would read as "yielded
// nothing" about a line that never claimed to be a count.

export interface AccountLine {
  /** `DOC-032`, `REQ-001`, `Product & features`, `sponsor`. */
  key: string;
  /** The line's own words, with a leading count clause stripped where there was one. */
  text: string;
  /** What the line claims it produced, when it claims anything. */
  tally: number | null;
  /** Zero-yield: the row renders in --ink-4. */
  muted: boolean;
  /** The bare heading this line sits under, when the discipline uses headings. */
  section: string | null;
}

export interface ParsedAccount {
  lines: AccountLine[];
  headings: string[];
  /** Lines the account states, total. */
  total: number;
  /** Lines that claim a yield of at least one. */
  yielded: number;
  /** Lines that say, in the account's own words, that the document was never read. */
  unread: number;
  /** `unread` is at least half the account — the guardrail was satisfied by a hollow answer. */
  hollow: boolean;
  /** The candour sentence, or null. Never invented: it states counts, not judgement. */
  warning: string | null;
}

/** `DOC-032: 4 proposals, 0 questions — text` */
const COUNTED = /^(\d+)\s+proposals?,\s*(\d+)\s+questions?\s*(?:—|--|-)\s*(.*)$/i;
/** `Product & features: covered by 3 proposals — text` */
const COVERED = /^covered by\s+(\d+)\s+proposals?\s*(?:—|--|-)?\s*(.*)$/i;
/** `stakeholder: 3 facts (…)`, `REQ-001: 1 criterion proposed — …` */
const LEADING = /^(\d+)\s+(proposals?|questions?|criteri(?:on|a)|facts?|events?|refs?)\b/i;

const NOTHING = /^nothing in the corpus\b/i;
const UNREAD = /\bnot (?:yet )?read\b/i;
const EMPTY_TEXT = /^(?:—|--|-|\(none(?: recorded)?\)|none|n\/a)\.?$/i;

/** The KEY of a `key: rest` line. Deliberately narrow so `REQ-001: Module 1: X` splits once. */
const ENTRY = /^([^:]{1,60}?):\s*(.*)$/;

function readTally(rest: string): { tally: number | null; text: string } {
  const counted = COUNTED.exec(rest);
  if (counted) return { tally: Number(counted[1]), text: counted[3].trim() || rest };

  const covered = COVERED.exec(rest);
  if (covered) return { tally: Number(covered[1]), text: covered[2].trim() || rest };

  const leading = LEADING.exec(rest);
  if (leading) return { tally: Number(leading[1]), text: rest };

  if (NOTHING.test(rest)) return { tally: 0, text: rest };
  return { tally: null, text: rest };
}

const isMuted = (tally: number | null, text: string): boolean =>
  tally === 0
  || EMPTY_TEXT.test(text.trim())
  || UNREAD.test(text)
  || NOTHING.test(text)
  || /^unmapped\b/i.test(text);

/**
 * Parse the stored account. An absent or blank account returns an empty parse — the CALLER
 * says whether that means "a discipline it never satisfied" or "a run that had none", because
 * only the caller knows `stats.close_discipline` (tab-agent IMPLEMENTATION, Zone 2).
 */
export function parseClosingAccount(raw: string | null | undefined): ParsedAccount {
  const lines: AccountLine[] = [];
  const headings: string[] = [];
  let section: string | null = null;

  for (const rawLine of String(raw ?? '').split('\n')) {
    const line = rawLine.trim();
    if (!line) continue;

    const entry = ENTRY.exec(line);
    if (!entry || !entry[2].trim()) {
      // A bare word, or `Category sweep:` with nothing after it — a heading, not a claim.
      const heading = (entry ? entry[1] : line).trim();
      if (heading) {
        headings.push(heading);
        section = heading;
      }
      continue;
    }

    const { tally, text } = readTally(entry[2].trim());
    lines.push({ key: entry[1].trim(), text, tally, muted: isMuted(tally, text), section });
  }

  const total = lines.length;
  const yielded = lines.filter((l) => (l.tally ?? 0) > 0).length;
  const unread = lines.filter((l) => UNREAD.test(l.text)).length;
  const hollow = total > 0 && unread * 2 >= total;

  return {
    lines,
    headings,
    total,
    yielded,
    unread,
    hollow,
    warning: hollow
      ? `close_pass validated that every in-scope ref appears in the account. It cannot validate `
        + `what the line SAYS. ${unread} of ${total} lines here say the document was not read.`
      : null,
  };
}

/** The one-line summary the account's head shows beside the zone label. */
export function accountHead(parsed: ParsedAccount, discipline: string | null): string {
  if (!parsed.total) return '';
  if (discipline === 'fact_namespaces') {
    const head = `${parsed.total} key${parsed.total === 1 ? '' : 's'} named across `
      + `${parsed.headings.length} namespace${parsed.headings.length === 1 ? '' : 's'}`;
    return parsed.yielded ? `${head}; ${parsed.yielded} carried a value.` : `${head}.`;
  }
  const noun = discipline === 'register' || discipline === 'register_acs' ? 'register item' : 'document';
  const head = `${parsed.total} ${noun}${parsed.total === 1 ? '' : 's'} accounted for`;
  if (parsed.unread) return `${head}; ${parsed.unread} of the lines say it was not read.`;
  if (parsed.yielded) return `${head}; ${parsed.yielded} carried a yield.`;
  return `${head}.`;
}
